"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  Bars,
  describe,
  FactsTable,
  Legend,
  NAME_KEYS,
  SummaryTable,
  summarize,
} from "@/components/tools/cs-tools/sorting-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberList } from "@/lib/parse-number";
import {
  ALGORITHM_IDS,
  type AlgorithmId,
  generateInput,
  MAX_ITEMS,
  MIN_ITEMS,
  PATTERNS,
  type Pattern,
  traceSort,
} from "@/lib/tool-kit/cs-tools/sorting";

/** English strings. A translated content file overrides these through `ui`. */
export const SORTING_ALGORITHM_VISUALIZER_STRINGS = {
  algorithmLabel: "Algorithm",
  nameBubble: "Bubble sort",
  nameInsertion: "Insertion sort",
  nameSelection: "Selection sort",
  nameMerge: "Merge sort",
  nameQuick: "Quick sort",
  nameHeap: "Heap sort",
  inputLabel: "Numbers to sort",
  inputHint:
    "Between {min} and {max} numbers, separated by commas, spaces or new lines. Negatives, decimals and repeated values all work.",
  inputDefault: "7, 3, 9, 1, 12, 5, 10, 2, 8, 4, 11, 6",
  generateHeading: "Or generate an input",
  patternLabel: "Pattern",
  patternRandom: "Random order",
  patternNearlySorted: "Nearly sorted",
  patternReversed: "Reversed",
  patternSorted: "Already sorted",
  patternFewUnique: "Few unique values",
  sizeLabel: "How many values",
  seedLabel: "Seed",
  seedHint:
    "The same seed and pattern always give the same input, so a run can be repeated or shared.",
  seedDefault: "2025",
  generate: "Generate from seed",
  newSeed: "New random seed",
  errCount: "Enter between {min} and {max} numbers. You have entered {count}.",
  numberWord: "one: number|other: numbers",
  errSeed: "The seed must be a whole number from 0 up.",
  stepBack: "Step back",
  stepForward: "Step forward",
  play: "Play",
  pause: "Pause",
  toEnd: "Jump to end",
  reset: "Reset",
  speedLabel: "Playback speed",
  speedSlow: "Slow",
  speedMedium: "Medium",
  speedFast: "Fast",
  reducedMotion:
    "Your device asks for reduced motion, so automatic playback is off. Use Step forward, or Jump to end.",
  resultsHeading: "Walkthrough",
  stepOf: "Step {step} of {total}",
  statComparisons: "Comparisons",
  statSwaps: "Swaps",
  statWrites: "Array writes",
  auxCaption: "Temporary copy of positions {lo} to {hi}",
  legendCompare: "Being compared",
  legendSwap: "Just swapped",
  legendWrite: "Just written",
  legendPivot: "Pivot, minimum or heap root",
  legendSettled: "In its final position",
  relGt: "greater than",
  relEq: "equal to",
  relLt: "less than",
  msgStart: "Start: {count} values, not yet sorted.",
  msgCompare: "Compare {left} with {right}.",
  msgCompareAt: "Compare {left} (position {at}) with {right} (position {at2}).",
  msgCompareResult: "{left} is {relation} {right}.",
  msgNewMin: "New smallest value so far: {value} at position {at}.",
  msgSwap: "Swap {a} (position {at}) with {b} (position {at2}).",
  msgWrite: "Write {value} to position {at}.",
  msgSettle: "{value} is now in its final position, {at}.",
  msgPivot:
    "Partition positions {lo} to {hi} around the pivot {value}, the last value of the range.",
  msgMerge:
    "Merge positions {lo} to {mid} with the sorted run that follows, up to position {hi}. The values are copied to a temporary buffer first.",
  msgHeapBuild:
    "Build a max-heap of {count} values: rearrange so that every parent is at least as large as its children.",
  msgHeapExtract:
    "The largest remaining value, {value}, is at the top of the heap. Swap it into position {at}.",
  msgNoSwaps: "Pass {pass} made no swaps, so everything is already in order.",
  msgDone: "Sorted: all {count} values are in order.",
  factsHeading: "About {name}",
  factsCaption: "Time and space cost",
  colProperty: "Property",
  colValue: "Value",
  factBest: "Best case time",
  factAverage: "Average case time",
  factWorst: "Worst case time",
  factSpace: "Extra space",
  factStable: "Stable (equal values keep their order)",
  yes: "Yes",
  no: "No",
  ideaBubble:
    "Compares neighbouring values and swaps any pair that is out of order, so the largest remaining value bubbles to the end on each pass. It stops early if a pass makes no swaps.",
  ideaInsertion:
    "Takes each value in turn and slides it left past larger values until it sits in place among those already sorted, the way you sort a hand of cards. It shifts values rather than swapping them, so it reports writes, not swaps.",
  ideaSelection:
    "Finds the smallest value in the unsorted part and swaps it into the next position of the sorted part. It always makes the same number of comparisons whatever the input.",
  ideaMerge:
    "Splits the list in half, sorts each half the same way, then merges the two sorted halves by repeatedly taking the smaller front value into a temporary buffer. The merged values are written back, so it reports writes, not swaps.",
  ideaQuick:
    "Uses the last value of a range as the pivot, moves smaller values to its left and larger to its right, then sorts each side. Because this version always takes the last value, input that is already sorted is its worst case.",
  ideaHeap:
    "Builds a max-heap, then repeatedly swaps the largest value, at the top of the heap, to the end and restores the heap on what remains.",
  summaryHeading: "All six on your input",
  summaryBody:
    "Operation counts when each algorithm sorts exactly the numbers above. Try the patterns to see the best and worst cases move.",
  summaryCaption: "Comparisons, swaps and writes by algorithm",
  colAlgorithm: "Algorithm",
} as const;

type Strings = typeof SORTING_ALGORITHM_VISUALIZER_STRINGS;

type Speed = "slow" | "medium" | "fast";

/** Slowest to fastest, all at or under 2.5 changes a second: no flashing. */
const SPEED_MS: Record<Speed, number> = { slow: 1200, medium: 700, fast: 400 };
const SIZES = [8, 12, 16, 24, 32, 40] as const;
const PLAIN: Intl.NumberFormatOptions = {
  maximumFractionDigits: 0,
  useGrouping: false,
};

const PATTERN_KEYS: Record<Pattern, keyof Strings> = {
  random: "patternRandom",
  nearlySorted: "patternNearlySorted",
  reversed: "patternReversed",
  sorted: "patternSorted",
  fewUnique: "patternFewUnique",
};

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

function randomSeed(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return (buffer[0] ?? 1) % 1_000_000;
}

export default function SortingAlgorithmVisualizerWidget() {
  const strings = useToolUi(SORTING_ALGORITHM_VISUALIZER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const reduced = useReducedMotion();
  const [algorithm, setAlgorithm] = useState<AlgorithmId>("bubble");
  const [text, setText] = useState<string>(strings.inputDefault);
  const [pattern, setPattern] = useState<Pattern>("random");
  const [size, setSize] = useState("12");
  const [seedText, setSeedText] = useState<string>(strings.seedDefault);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>("medium");

  const numbers = useMemo(() => parseNumberList(text), [text]);
  const valid = numbers.length >= MIN_ITEMS && numbers.length <= MAX_ITEMS;
  const steps = useMemo(
    () => (valid ? traceSort(algorithm, numbers) : []),
    [algorithm, numbers, valid]
  );
  const summary = useMemo(
    () => (valid ? summarize(numbers) : []),
    [numbers, valid]
  );
  const lastIndex = Math.max(0, steps.length - 1);
  const current = steps[Math.min(position, lastIndex)];

  useEffect(() => {
    if (!playing) {
      return undefined;
    }
    if (reduced || position >= lastIndex) {
      setPlaying(false);
      return undefined;
    }
    const timer = setTimeout(() => setPosition(position + 1), SPEED_MS[speed]);
    return () => clearTimeout(timer);
  }, [playing, position, lastIndex, speed, reduced]);

  function restart() {
    setPosition(0);
    setPlaying(false);
  }

  function chooseAlgorithm(next: AlgorithmId) {
    setAlgorithm(next);
    restart();
  }

  function editText(next: string) {
    setText(next);
    restart();
  }

  const seedValue = parseIntegerInput(seedText);
  const seedValid = seedValue !== null && seedValue >= 0;

  function generateWith(seed: number) {
    editText(generateInput(pattern, Number(size), seed).join(", "));
  }

  function newSeed() {
    const seed = randomSeed();
    setSeedText(String(seed));
    generateWith(seed);
  }

  const limits = { min: fmt(MIN_ITEMS, PLAIN), max: fmt(MAX_ITEMS, PLAIN) };

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.algorithmLabel}
        name="sort-algorithm"
        onChange={chooseAlgorithm}
        options={ALGORITHM_IDS.map((id) => ({
          value: id,
          label: strings[NAME_KEYS[id]],
        }))}
        value={algorithm}
      />
      <TextAreaField
        hint={t(strings.inputHint, limits)}
        id="sort-input"
        label={strings.inputLabel}
        onChange={editText}
        rows={2}
        value={text}
      />
      <fieldset className="space-y-3">
        <legend className="font-medium text-foreground text-sm">
          {strings.generateHeading}
        </legend>
        <div className="grid gap-4 md:grid-cols-3">
          <SelectField
            id="sort-pattern"
            label={strings.patternLabel}
            onChange={setPattern}
            options={PATTERNS.map((id) => ({
              value: id,
              label: strings[PATTERN_KEYS[id]],
            }))}
            value={pattern}
          />
          <SelectField
            id="sort-size"
            label={strings.sizeLabel}
            onChange={setSize}
            options={SIZES.map((n) => ({
              value: String(n),
              label: fmt(n, PLAIN),
            }))}
            value={size}
          />
          <TextField
            hint={strings.seedHint}
            id="sort-seed"
            label={strings.seedLabel}
            mono
            onChange={setSeedText}
            value={seedText}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={!seedValid}
            onClick={() => generateWith(seedValue ?? 0)}
            type="button"
          >
            {strings.generate}
          </button>
          <button className={BUTTON_SECONDARY} onClick={newSeed} type="button">
            {strings.newSeed}
          </button>
        </div>
        {seedValid ? null : <ErrorNotice>{strings.errSeed}</ErrorNotice>}
      </fieldset>

      {valid && current ? (
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          <Controls
            lastIndex={lastIndex}
            onPlay={() => setPlaying(!playing)}
            onPosition={(next) => {
              setPlaying(false);
              setPosition(next);
            }}
            onSpeed={setSpeed}
            playing={playing}
            position={position}
            reduced={reduced}
            speed={speed}
            strings={strings}
          />
          <Bars fmt={fmt} step={current} strings={strings} />
          <Legend strings={strings} />
          <p
            aria-live={playing ? "off" : "polite"}
            className="rounded-lg border border-border bg-card p-3 text-foreground text-sm"
          >
            <span className="mr-2 font-medium text-muted-foreground">
              {t(strings.stepOf, {
                step: fmt(position + 1, PLAIN),
                total: fmt(steps.length, PLAIN),
              })}
            </span>
            {describe(current.message, strings, fmt)}
          </p>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.statComparisons}
              value={fmt(current.comparisons, PLAIN)}
            />
            <StatCard
              label={strings.statSwaps}
              value={fmt(current.swaps, PLAIN)}
            />
            <StatCard
              label={strings.statWrites}
              value={fmt(current.writes, PLAIN)}
            />
          </StatGrid>
          <FactsTable id={algorithm} strings={strings} />
          <SummaryTable fmt={fmt} strings={strings} summary={summary} />
        </section>
      ) : (
        <ErrorNotice>
          {t(strings.errCount, {
            ...limits,
            count: `${fmt(numbers.length, PLAIN)}${tPlural(strings.numberWord, locale, numbers.length)}`,
          })}
        </ErrorNotice>
      )}
    </div>
  );
}

function Controls({
  strings,
  position,
  lastIndex,
  playing,
  speed,
  reduced,
  onPlay,
  onPosition,
  onSpeed,
}: {
  strings: Strings;
  position: number;
  lastIndex: number;
  playing: boolean;
  speed: Speed;
  reduced: boolean;
  onPlay: () => void;
  onPosition: (next: number) => void;
  onSpeed: (speed: Speed) => void;
}) {
  const atEnd = position >= lastIndex;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={position === 0}
          onClick={() => onPosition(Math.max(0, position - 1))}
          type="button"
        >
          {strings.stepBack}
        </button>
        <button
          className={BUTTON_PRIMARY}
          disabled={atEnd}
          onClick={() => onPosition(position + 1)}
          type="button"
        >
          {strings.stepForward}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={reduced || atEnd}
          onClick={onPlay}
          type="button"
        >
          {playing ? strings.pause : strings.play}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={atEnd}
          onClick={() => onPosition(lastIndex)}
          type="button"
        >
          {strings.toEnd}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={position === 0}
          onClick={() => onPosition(0)}
          type="button"
        >
          {strings.reset}
        </button>
        <div className="w-40">
          <SelectField
            id="sort-speed"
            label={strings.speedLabel}
            onChange={onSpeed}
            options={[
              { value: "slow", label: strings.speedSlow },
              { value: "medium", label: strings.speedMedium },
              { value: "fast", label: strings.speedFast },
            ]}
            value={speed}
          />
        </div>
      </div>
      {reduced ? <Notice>{strings.reducedMotion}</Notice> : null}
    </div>
  );
}
